import { formatDateTime, formatRelative } from "../../lib/format";
import type { ActivityRow } from "../../lib/api/types";
import { ActionPill } from "./ActionPill";

interface ActivityTableProps {
  rows: ActivityRow[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onSelectUser?: (userId: string) => void;
  onSelectFile?: (fileId: string) => void;
  emptyCopy?: string;
  isLoading?: boolean;
}

const DEFAULT_EMPTY = "No activity yet. Events appear here when investors open or download files.";

export function ActivityTable({
  rows,
  total,
  page,
  pageSize,
  onPageChange,
  onSelectUser,
  onSelectFile,
  emptyCopy = DEFAULT_EMPTY,
  isLoading = false,
}: ActivityTableProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  if (!isLoading && rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-surface-border bg-surface p-8 text-center text-body text-text-muted">
        {emptyCopy}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto rounded-lg border border-surface-border bg-surface">
        <table className="w-full min-w-[640px] border-collapse text-left">
          <thead>
            <tr className="border-b border-surface-border text-meta text-text-muted">
              <th className="px-3 py-2 font-medium">When</th>
              <th className="px-3 py-2 font-medium">Investor</th>
              <th className="px-3 py-2 font-medium">Action</th>
              <th className="px-3 py-2 font-medium">File</th>
              <th className="px-3 py-2 font-medium">Folder</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-surface-border last:border-0">
                <td className="whitespace-nowrap px-3 py-2 text-body text-text">
                  <span title={formatRelative(r.occurredAt)}>{formatDateTime(r.occurredAt)}</span>
                </td>
                <td className="px-3 py-2 text-body">
                  {onSelectUser ? (
                    <button
                      type="button"
                      onClick={() => onSelectUser(r.userId)}
                      className="font-medium text-brand-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    >
                      {r.displayName}
                    </button>
                  ) : (
                    <span className="font-medium text-text">{r.displayName}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <ActionPill action={r.action} />
                </td>
                <td className="px-3 py-2 text-body">
                  {onSelectFile && r.fileId ? (
                    <button
                      type="button"
                      onClick={() => onSelectFile(r.fileId as string)}
                      className="text-text hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    >
                      {r.fileName}
                    </button>
                  ) : (
                    <span className="text-text">{r.fileName}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-body text-text-muted">{r.folderPath}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-meta text-text-muted">
        <span>
          {from}–{to} of {total}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="rounded-md border border-surface-border px-2 py-1 text-body text-text disabled:cursor-not-allowed disabled:opacity-50"
          >
            Prev
          </button>
          <span>
            Page {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className="rounded-md border border-surface-border px-2 py-1 text-body text-text disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
